import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { subscriptionsApi } from '../../api/subscriptions';
import { SubscriptionPlan } from '../../types/subscription.types';
import PlanPickerCard from '../../components/subscriptions/PlanPicker/PlanPickerCard';
import TrialStepsHeader from '../../components/subscriptions/PlanPicker/TrialStepsHeader';

const PLAN_ORDER = ['starter', 'growth'];
const LAST_SELECTED_PLAN_KEY = 'webleads_start_trial_selected_plan';

const formatTrialEndDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 5);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
};

const StartTrial: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const posthog = usePostHog();
  const [searchParams, setSearchParams] = useSearchParams();

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [trialEligible, setTrialEligible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [showAbandonedBanner, setShowAbandonedBanner] = useState(searchParams.get('canceled') === 'true');
  const viewedRef = useRef(false);
  const paywallViewedRef = useRef(false);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    posthog?.capture('trial_plan_picker_viewed');
  }, [posthog]);

  useEffect(() => {
    if (loading || trialEligible || paywallViewedRef.current) return;
    paywallViewedRef.current = true;
    posthog?.capture('paywall_viewed');
  }, [loading, trialEligible, posthog]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [{ plans: fetchedPlans }, { trial_eligible: eligible }] = await Promise.all([
          subscriptionsApi.getPlans(),
          subscriptionsApi.getCurrentSubscription(),
        ]);

        const sortedPlans = [...fetchedPlans]
          .filter((p) => PLAN_ORDER.includes(p.name))
          .sort((a, b) => PLAN_ORDER.indexOf(a.name) - PLAN_ORDER.indexOf(b.name));

        setPlans(sortedPlans);
        setTrialEligible(eligible);

        const storedPlanName = sessionStorage.getItem(LAST_SELECTED_PLAN_KEY);
        const storedPlan = storedPlanName ? sortedPlans.find((p) => p.name === storedPlanName) : undefined;
        const defaultPlan = storedPlan ?? sortedPlans.find((p) => p.name === 'starter') ?? sortedPlans[0];
        if (defaultPlan) setSelectedPlanId(defaultPlan.id);
      } catch (error) {
        console.error('Failed to load plan picker data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const trialEndDate = useMemo(() => formatTrialEndDate(), []);

  const handleSelectPlan = (planId: number) => {
    setSelectedPlanId(planId);
    const plan = plans.find((p) => p.id === planId);
    if (plan) {
      sessionStorage.setItem(LAST_SELECTED_PLAN_KEY, plan.name);
      posthog?.capture('trial_plan_selected', { plan: plan.name });
    }
  };

  const dismissAbandonedBanner = () => {
    setShowAbandonedBanner(false);
    searchParams.delete('canceled');
    setSearchParams(searchParams, { replace: true });
  };

  const handleStartCheckout = async (planId: number) => {
    const plan = plans.find((p) => p.id === planId);
    if (!plan || redirecting) return;

    setSelectedPlanId(plan.id);
    sessionStorage.setItem(LAST_SELECTED_PLAN_KEY, plan.name);
    setRedirecting(true);
    posthog?.capture('checkout_started', { plan: plan.name });

    try {
      const successUrl = `${window.location.origin}/start-trial/return`;
      const cancelUrl = `${window.location.origin}/start-trial?canceled=true`;
      const { checkout_url } = await subscriptionsApi.createCheckoutSession(plan.id, successUrl, cancelUrl);
      if (checkout_url) {
        window.location.href = checkout_url;
      } else {
        setRedirecting(false);
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      setRedirecting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div
      className="min-h-screen bg-light px-4 py-10 sm:px-8 sm:py-12"
      style={{
        backgroundImage:
          "linear-gradient(0deg, rgba(249,245,245,.9), rgba(249,245,245,.9)), url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22><path d=%22M0 20h40M20 0v40%22 stroke=%22%234BA1C5%22 stroke-width=%221%22 stroke-opacity=%22.06%22/></svg>')",
      }}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between pb-6">
        <span className="text-xl font-extrabold tracking-tight text-primary">WebLeads</span>
        <button onClick={handleLogout} className="text-sm font-medium text-gray-500 hover:text-primary">
          Log out
        </button>
      </div>

      <div className="mx-auto max-w-6xl">
        <TrialStepsHeader />

        <h1 className="text-center text-3xl font-extrabold tracking-tight text-secondary sm:text-4xl">
          Start your 5-day trial
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-center text-[15px] leading-relaxed text-gray-500">
          {trialEligible ? (
            <>
              Your normal monthly price starts on <b className="font-bold text-accent">{trialEndDate}</b>. Cancel
              anytime before then and you won&apos;t be charged again.
            </>
          ) : (
            'Billed monthly. Cancel anytime.'
          )}
        </p>

        {showAbandonedBanner && (
          <div className="mx-auto mt-6 flex max-w-2xl items-start justify-between gap-3 rounded-xl border border-accent/30 bg-accent/10 px-5 py-3.5 text-sm text-secondary">
            <span>No charge was made. Pick a plan whenever you&apos;re ready - your account isn&apos;t going anywhere.</span>
            <button onClick={dismissAbandonedBanner} className="flex-none text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : (
          <div className="mx-auto mt-9 grid max-w-3xl grid-cols-1 items-stretch gap-5 md:grid-cols-2">
            {plans.map((plan) => (
              <PlanPickerCard
                key={plan.id}
                plan={plan}
                allPlans={plans}
                isSelected={plan.id === selectedPlanId}
                ctaLabel={
                  trialEligible && plan.trial_fee_display
                    ? `Start 5-day trial · ${plan.trial_fee_display}`
                    : 'Subscribe'
                }
                ctaBusy={redirecting}
                trialEligible={trialEligible}
                onSelect={handleSelectPlan}
                onCheckout={handleStartCheckout}
              />
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-wrap justify-center gap-x-7 gap-y-2.5">
          {[
            'Full plan limits during trial',
            'Full price starts on day 5, no surprises',
            'Refundable on request, no questions asked',
          ].map((t) => (
            <span key={t} className="flex items-center gap-1.5 text-[12.5px] text-gray-500">
              <span className="h-1.5 w-1.5 rounded-full bg-map-green" />
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StartTrial;
